import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import authService from '../services/authService';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Initialize form with current user data
    if (user) {
      setFullName(user.full_name || '');
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!fullName.trim()) {
      setError('Full name is required');
      return;
    }

    if (fullName.trim().length < 3) {
      setError('Full name must be at least 3 characters');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await authService.updateProfile(fullName.trim());

      if (response.message) {
        setSuccess(response.message);

        // Merge in the updated name only — don't let the backend response
        // overwrite the session role (e.g. a Reviewer logged in as Inspector)
        if (response.user) {
          updateUser({ ...user, full_name: response.user.full_name });
        }

        // Redirect back to dashboard after 2 seconds
        setTimeout(() => {
          navigate(`/dashboard/${user?.role}`);
        }, 2000);
      }
    } catch (err) {
      setError(err.error || err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/dashboard/${user?.role}`);
  };

  return (
    <>
      <Navbar />
      <Container className="mt-5" style={{ paddingTop: '60px', maxWidth: '600px' }}>
        <Card className="shadow">
          <Card.Header className="bg-primary text-white">
            <h4 className="mb-0">
              <i className="fas fa-user-edit me-2"></i>
              Edit Profile
            </h4>
          </Card.Header>
          <Card.Body>
            {/* Success Alert */}
            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                <i className="fas fa-check-circle me-2"></i>
                {success}
              </Alert>
            )}

            {/* Error Alert */}
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
              </Alert>
            )}

            {/* Profile Form */}
            <Form onSubmit={handleSubmit}>
              {/* Current User Info (Read-only) */}
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="text-muted mb-3">Current Information</h6>
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Employee Code:</small>
                    <p className="mb-0 fw-bold">{user?.emp_code}</p>
                  </div>
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Email:</small>
                    <p className="mb-0 fw-bold">{user?.email}</p>
                  </div>
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Role:</small>
                    <p className="mb-0 fw-bold">
                      {{ jr_inspector: 'Inspector', sr_inspector: 'Reviewer', admin: 'Admin' }[user?.role] || user?.role}
                    </p>
                  </div>
                  <div className="col-md-6 mb-2">
                    <small className="text-muted">Status:</small>
                    <p className="mb-0">
                      <span className={`badge ${user?.email_verified ? 'bg-success' : 'bg-warning'}`}>
                        {user?.email_verified ? 'Verified' : 'Not Verified'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Editable Fields */}
              <Form.Group className="mb-4">
                <Form.Label>
                  Full Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={loading}
                  required
                  minLength={3}
                />
                <Form.Text className="text-muted">
                  This name will be displayed on your profile and certificates.
                </Form.Text>
              </Form.Group>

              {/* Action Buttons */}
              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                  className="flex-grow-1"
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Update Profile
                    </>
                  )}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </Button>
              </div>
            </Form>

            {/* Note */}
            <div className="mt-4 p-3 border border-info rounded bg-light">
              <small className="text-muted">
                <i className="fas fa-info-circle me-2 text-info"></i>
                <strong>Note:</strong> Employee Code, Email, and Role cannot be changed.
                If you need to update these fields, please contact the administrator.
              </small>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default EditProfile;
